/**
 * usePollingQuotes — polls the REST /market/watchlist endpoint at a fixed
 * interval, diffs prices against the previous fetch, and exposes the same
 * shape as useLiveQuotes so callers don't need to change.
 *
 * Why polling instead of WebSocket?
 * Alpaca's free IEX data WebSocket allows only one connection per account
 * and many paper accounts no longer have IEX data enabled at all. REST
 * works for every account tier without rate-limit issues at 1 req / 3 sec.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const POLL_MS = 3000;

export function usePollingQuotes(symbols) {
  const [quotes, setQuotes] = useState({});
  const [connected, setConnected] = useState(false);
  const [tickCount, setTickCount] = useState(0);
  const [lastTickAt, setLastTickAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  const prevPricesRef = useRef({});
  const symbolsKey = (symbols || []).slice().sort().join(",");

  useEffect(() => {
    if (!symbolsKey) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await api.get("/market/watchlist");
        if (cancelled) return;
        const arr = r.data || [];
        const next = {};
        let changedCount = 0;
        const prev = prevPricesRef.current;
        for (const q of arr) {
          if (!q || !q.symbol) continue;
          const prevPrice = prev[q.symbol];
          const changed = prevPrice != null && prevPrice !== q.price;
          if (changed) changedCount += 1;
          next[q.symbol] = {
            price: q.price,
            bid: q.price,
            ask: q.price,
            demo: !!q.demo,
            kind: "poll",
            lastUpdate: changed ? Date.now() : prev[q.symbol + "_lu"] || Date.now(),
          };
        }
        // Save current prices for next diff
        const newPrev = {};
        for (const q of arr) {
          if (q && q.symbol) {
            newPrev[q.symbol] = q.price;
            newPrev[q.symbol + "_lu"] = next[q.symbol].lastUpdate;
          }
        }
        prevPricesRef.current = newPrev;
        setQuotes(next);
        setConnected(true);
        setLastError(null);
        if (changedCount > 0) {
          setTickCount((n) => n + changedCount);
          setLastTickAt(Date.now());
        }
      } catch (err) {
        if (cancelled) return;
        setConnected(false);
        setLastError(err?.response?.data?.detail || err.message || "poll failed");
      }
    };

    // initial fetch + interval
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [symbolsKey]);

  return {
    quotes,
    connected,
    tickCount,
    lastTickAt,
    lastError,
    demoMode: false,
  };
}
